import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

/** @param {{ section?: "leads" | "smtp" }} props */
export default function CustomerSettings({ section = "leads" }) {
  const isSmtp = section === "smtp";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [leadsOptions, setLeadsOptions] = useState("from_list");
  const [smtp, setSmtp] = useState({
    enabled: false, host: "", port: 587, username: "", password: "",
    from_email: "", from_name: "", reply_to: "", use_tls: true, use_ssl: false,
    password_configured: false, password_hint: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer-settings");
      setLeadsOptions(data.leads_options || "from_list");
      setSmtp((s) => ({ ...s, ...(data.smtp || {}), password: "" }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveLeads = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/customer-settings/general", {
        leads_options: leadsOptions,
      });
      setLeadsOptions(data.leads_options || "from_list");
      toast.success("Leads options saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const saveSmtp = async () => {
    setSaving(true);
    try {
      const body = { ...smtp };
      if (!body.password) delete body.password;
      delete body.password_configured;
      delete body.password_hint;
      const { data } = await api.put("/customer-settings/smtp", body);
      setSmtp((s) => ({ ...s, ...data, password: "" }));
      toast.success("SMTP settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const testSmtp = async () => {
    setTesting(true);
    try {
      const body = { ...smtp };
      if (!body.password) delete body.password;
      delete body.password_configured;
      delete body.password_hint;
      const { data } = await api.post("/customer-settings/smtp/test", body);
      toast.success(data.message || "SMTP test succeeded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading settings…</div>;
  }

  if (isSmtp) {
    return (
      <div className="space-y-6" data-testid="customer-smtp-page">
        <PageHeader
          title="SMTP"
          subtitle="Outbound mail for account notifications"
        />
        <div className="space-y-4 rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between max-w-lg">
            <div>
              <p className="text-sm font-medium">Enable SMTP</p>
              <p className="text-xs text-muted-foreground">Use your own outbound mail for account notifications.</p>
            </div>
            <Switch
              checked={!!smtp.enabled}
              onCheckedChange={(v) => setSmtp((s) => ({ ...s, enabled: v }))}
              data-testid="cust-smtp-enabled"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 max-w-2xl">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>SMTP host</Label>
              <Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} data-testid="cust-smtp-host" />
            </div>
            <div className="space-y-1.5">
              <Label>Port</Label>
              <Input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value || "587", 10) })} data-testid="cust-smtp-port" />
            </div>
            <div className="space-y-1.5">
              <Label>Username</Label>
              <Input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} data-testid="cust-smtp-username" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Password {smtp.password_configured ? <span className="text-muted-foreground font-normal">({smtp.password_hint || "saved"} — leave blank to keep)</span> : null}</Label>
              <Input type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} placeholder={smtp.password_configured ? "••••••••" : ""} data-testid="cust-smtp-password" autoComplete="new-password" />
            </div>
            <div className="space-y-1.5">
              <Label>From email</Label>
              <Input value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} data-testid="cust-smtp-from-email" />
            </div>
            <div className="space-y-1.5">
              <Label>From name</Label>
              <Input value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} data-testid="cust-smtp-from-name" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Reply-To</Label>
              <Input value={smtp.reply_to} onChange={(e) => setSmtp({ ...smtp, reply_to: e.target.value })} data-testid="cust-smtp-reply-to" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!smtp.use_tls} onChange={(e) => setSmtp({ ...smtp, use_tls: e.target.checked })} data-testid="cust-smtp-tls" />
              STARTTLS
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!smtp.use_ssl} onChange={(e) => setSmtp({ ...smtp, use_ssl: e.target.checked })} data-testid="cust-smtp-ssl" />
              SSL
            </label>
          </div>
          <div className="flex gap-2">
            <Button className="rounded-lg" onClick={saveSmtp} disabled={saving} data-testid="cust-save-smtp">
              {saving ? "Saving…" : "Save SMTP"}
            </Button>
            <Button variant="outline" className="rounded-lg" onClick={testSmtp} disabled={testing} data-testid="cust-test-smtp">
              {testing ? "Testing…" : "Send test"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-settings-page">
      <PageHeader
        title="Leads Options"
        subtitle="Choose whether leads are managed from Lists or from the Leads section"
      />
      <div className="space-y-4 rounded-xl border border-border bg-card p-5">
        <p className="text-sm text-muted-foreground max-w-xl">
          Changing this does not delete or migrate existing lead records.
        </p>
        <div className="space-y-3 max-w-lg">
          <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer">
            <input
              type="radio"
              name="leads_options"
              className="mt-1"
              checked={leadsOptions === "from_list"}
              onChange={() => setLeadsOptions("from_list")}
              data-testid="leads-opt-from-list"
            />
            <div>
              <p className="text-sm font-medium">From List</p>
              <p className="text-xs text-muted-foreground">View, add, edit, and import leads through Lists.</p>
            </div>
          </label>
          <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer">
            <input
              type="radio"
              name="leads_options"
              className="mt-1"
              checked={leadsOptions === "from_lead"}
              onChange={() => setLeadsOptions("from_lead")}
              data-testid="leads-opt-from-lead"
            />
            <div>
              <p className="text-sm font-medium">From Lead</p>
              <p className="text-xs text-muted-foreground">View, add, edit, and import leads through Leads.</p>
            </div>
          </label>
        </div>
        <Button className="rounded-lg" onClick={saveLeads} disabled={saving} data-testid="cust-save-leads">
          {saving ? "Saving…" : "Save Leads Options"}
        </Button>
      </div>
    </div>
  );
}
